import React, { createElement, useMemo } from "react"
import { cva } from "class-variance-authority"
import cn from "../../../utils/classes"

export type HeaderType =
  | "xlHeader"
  | "headerTitle"
  | "pageHeader"
  | "sectionHeader"
  | "thirdLevelHeader"
  | "fourthLevelHeader"

export interface SemanticHeaderProps {
  /** The text to be displayed in the header. */
  text: string
  /** The level of the header element to be rendered. */
  headerLevel: "h1" | "h2" | "h3" | "h4" | "h5"
  /** The type of the header. Determines the header element to be rendered. */
  type?: HeaderType
  /** The id for the header element. */
  id?: string
  /** Additional styles to be applied to the header element. */
  styleOverrides?: string
  /** Indicates if the header is required and adds a red asterisk */
  isRequired?: boolean
}

/**
 * SemanticHeader component.
 *
 * This component renders a header element based on the provided type.
 *
 * @component
 * @param {React.PropsWithChildren<SemanticHeaderProps>} props The properties for the component.
 */
export const SemanticHeader: React.FC<
  React.PropsWithChildren<SemanticHeaderProps>
> = ({
  text,
  headerLevel,
  type = "pageHeader",
  id,
  styleOverrides,
  isRequired,
}) => {
  const variants = cva([], {
    variants: {
      type: {
        xlHeader: "!text-6 text-gray-900 font-bold leading-8",
        headerTitle: "!text-5 text-gray-700 font-semibold leading-7",
        pageHeader: "!text-4.5 text-primary-blue font-semibold leading-8",
        sectionHeader: "!text-4 text-gray-900 font-semibold leading-6",
        thirdLevelHeader: "!text-4 text-gray-700 font-normal leading-6",
        fourthLevelHeader: "!text-sm text-gray-700 font-normal leading-6",
      },
    },
  })

  const defaultStyles = ""

  const className = useMemo(() => {
    return cn(variants({ type }), defaultStyles, styleOverrides)
  }, [type, styleOverrides, variants])

  return createElement(
    headerLevel,
    { className, id },
    isRequired ? (
      <>
        <span className="text-red-600 !mr-1" aria-hidden="true">
          *
        </span>
        {text}
      </>
    ) : (
      text
    ),
  )
}
